import React, { useState } from 'react';
import { Filter, Search, ChevronDown, ChevronUp } from 'lucide-react';
import DateRangeFilter from './filters/DateRangeFilter';
import AgeFilter from './filters/AgeFilter';
import MoreFilters from './filters/MoreFilters';
import { FilterInfo } from '../utils/types';
import { useCampContext } from '../context/CampContext';
import './FilterBar.css';

export interface FilterOptions {
  startDate?: string;
  endDate?: string;
  ages?: number[];
  minPrice?: number;
  maxPrice?: number;
  activityKeywords?: string[];
  duration?: 'full-day' | 'half-day';
  minRating?: number;
}

interface FilterBarProps {
  onFiltersChange: (filters: FilterOptions) => void;
  onClearAll: () => void;
}

const FilterBar: React.FC<FilterBarProps> = ({ 
  onFiltersChange,
  onClearAll
}) => {
  const { filters, setFilters } = useCampContext();
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);

  // Helper function to format date for display
  const formatDateForDisplay = (dateString?: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Helper function to format ages for display
  const formatAgesForDisplay = (ages?: number[]) => {
    if (!ages || ages.length === 0) return null;
    if (ages.length === 1) return `Age ${ages[0]}`;
    if (ages.length === 2) return `Ages ${ages[0]}-${ages[1]}`;
    return `Ages: ${Math.min(...ages)}-${Math.max(...ages)}`;
  };

  // Generate mobile filter summary
  const getMobileFilterSummary = () => {
    const parts = [];
    
    if (filters.startDate && filters.endDate) {
      const startDate = formatDateForDisplay(filters.startDate);
      const endDate = formatDateForDisplay(filters.endDate);
      parts.push(`Dates: ${startDate} - ${endDate}`);
    }
    
    const ages = formatAgesForDisplay(filters.ages);
    if (ages) {
      parts.push(ages);
    }
    
    return parts.length > 0 ? parts.join(' • ') : 'Showing camps for all dates and ages.';
  };

  const handleDateRangeChange = (startDate: Date | null, endDate: Date | null) => {
    // Reset start and end date.
    if (!startDate && !endDate) {
      const newFilters: FilterOptions = {
        ...filters,
        startDate: undefined,
        endDate: undefined
      };
      setFilters(newFilters);
      onFiltersChange(newFilters);
    }
    // Set start and end date if they are valid.
    if (startDate && endDate && startDate <= endDate) {
      const newFilters: FilterOptions = {
        ...filters,
        startDate: startDate ? startDate.toISOString().split('T')[0] : undefined,
        endDate: endDate ? endDate.toISOString().split('T')[0] : undefined
      };
      setFilters(newFilters);
      onFiltersChange(newFilters);
    }
  };


  const handleFiltersChange = (filters: FilterOptions) => {
    const newFilters: FilterOptions = {
      ...filters,
    };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleSearch = () => {
    onFiltersChange(filters);
  };

  const handleAgesChange = (ages: number[]) => {
    const newFilters: FilterOptions = {
      ...filters,
      ages: ages
    };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  return (
    <div className="filter-bar">
      {/* Mobile collapsible view */}
      <div className="filter-bar-mobile">
        <div className="filter-bar-mobile-container">
          <button 
            className="filter-bar-mobile-toggle"
            onClick={() => setIsMobileExpanded(!isMobileExpanded)}
          >
            <div className="filter-bar-mobile-content">
              <div className="filter-bar-mobile-label">
                <Filter className="icon icon--sm" />
                {getMobileFilterSummary()}
              </div>
            </div>
            {isMobileExpanded ? (
              <ChevronUp className="icon icon--sm" />
            ) : (
              <ChevronDown className="icon icon--sm" />
            )}
          </button>
          
          {isMobileExpanded && (
            <div className="filter-bar-mobile-filters">
              <DateRangeFilter 
                onDateRangeChange={handleDateRangeChange}
                initialStartDate={filters.startDate}
                initialEndDate={filters.endDate}
              />
              
              <AgeFilter 
                onAgesChange={handleAgesChange}
                initialAges={filters?.ages || []}
              />
              
              <MoreFilters 
                onFiltersChange={handleFiltersChange}
                onClearAll={onClearAll}
              />
            </div>
          )}
        </div>
      </div>

      {/* Desktop view */}
      <div className="filter-bar-desktop">
        <div className="filter-bar-content">
          <div className="filter-label">
            <Filter className="icon icon--sm" />
            <span>Filters:</span>
          </div>
          
          <div className="filter-buttons">
            <DateRangeFilter 
              onDateRangeChange={handleDateRangeChange}
              initialStartDate={filters.startDate}
              initialEndDate={filters.endDate}
            />
            
            <AgeFilter 
              onAgesChange={handleAgesChange}
              initialAges={filters?.ages || []}
            />
            
            <MoreFilters 
              onFiltersChange={handleFiltersChange}
              onClearAll={onClearAll}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
